import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { KText } from '@/ui-library/components/text';
import { UNDERWAY_POPUP_STRINGS } from './underwayPopup.strings';
import { BULK_OPERATION_QUEUE_Z_INDEX } from '@/components/ats/BulkOperationQueue';

const STR = UNDERWAY_POPUP_STRINGS.stopConfirm;

// One above the bulk-queue popup that hosts this dialog's trigger — the popup
// sits above drawers/modals, so its own dialogs must sit above the popup.
const MODAL_Z_INDEX = BULK_OPERATION_QUEUE_Z_INDEX + 1;

export type StopAutomationConfirmModalProps = {
  isOpen: boolean;
  // Single-candidate moves pass `candidateName`; bulk passes a count
  // (`candidateName` is left undefined). `stageName` is shared.
  candidateName?: string;
  candidateCount: number;
  stageName: string;
  onCancel: () => void;
  onConfirm: () => void;
};

export const StopAutomationConfirmModal = ({
  isOpen,
  candidateName,
  candidateCount,
  stageName,
  onCancel,
  onConfirm,
}: StopAutomationConfirmModalProps) => {
  const isSingle = candidateCount === 1 && Boolean(candidateName);
  const body = isSingle
    ? STR.bodySingle(candidateName as string, stageName)
    : STR.bodyBulk(candidateCount, stageName);
  return (
    <Modal isOpen={isOpen} onClose={onCancel} isCentered size="sm">
      <ModalOverlay zIndex={MODAL_Z_INDEX} />
      <ModalContent
        containerProps={{ zIndex: MODAL_Z_INDEX }}
        borderRadius="lg"
        data-test-id="automation-stop-confirm-modal"
      >
        <ModalHeader pb="0">
          <KText size="md-semi-bold" color="secondary-900">
            {STR.title}
          </KText>
        </ModalHeader>
        <ModalBody pt="8px" pb="16px">
          <KText size="sm-regular" color="secondary-600">
            {body}
          </KText>
        </ModalBody>
        <ModalFooter gap="8px">
          <Flex gap="8px" justifyContent="flex-end" w="100%">
            <Button
              variant="secondary"
              onClick={onCancel}
              data-test-id="automation-stop-cancel-button"
            >
              {STR.cancel}
            </Button>
            <Button
              variant="primary"
              bg="error-200"
              color="base-white"
              _hover={{ bg: 'error-100' }}
              onClick={onConfirm}
              data-test-id="automation-stop-confirm-button"
            >
              {STR.stop}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
